import {
  ListMessage,
  updateMessage,
  sendMessage,
  getContact,
  getContacts,
  sendStagedMessage,
  rejectStagedMessage,
  editStagedMessage,
  generateSuggestedResponse,
  setSuggestedResponseModel,
  editSuggestedResponse,
  dismissSuggestedResponse,
  acceptSuggestedResponse,
} from "@store/features/chat/chatSlice";
import { getWorkflowRun } from "@store/features/settings/workflowSlice";
import { open as openSideNavigation } from "@store/features/layout/sideNavigationSlice";
import { type GetContactOutputType } from "skedyul-core/features/conversations";
import { iconTypes } from "skedyul-ui/ui";
import { store } from "@store/index";
import { selectSubscription } from "@store/features/chat/chatSlice";
import { MessageComponent } from "skedyul-ui/src/components/ui/ShadcnChat/components/Message";
import dayjs from "dayjs";
import { type TeamMember } from "@store/features/account/workplaceSlice";
import { ChatInputBarProps } from "skedyul-ui/src/components/ui/ShadcnChat/components/ChatInputBar";
import { ChatListProps } from "skedyul-ui/src/components/ui/ShadcnChat/components/ChatList";
import { ListProps } from "skedyul-ui/src/components/ui/List";
import { DefaultMessageProps } from "skedyul-ui/src/components/ui/ShadcnChat/components/Message";
import { StagedMessageProps } from "skedyul-ui/src/components/ui/ShadcnChat/components/StagedMessage";
import { SuggestedResponseProps } from "skedyul-ui/src/components/ui/ShadcnChat/components/SuggestedResponse";
import { AppRouterInstance } from "next/dist/shared/lib/app-router-context.shared-runtime";
import { generateId } from "skedyul-core/tools";
import { trpc } from "@utils/api";
import { open as openDialog } from "@store/features/layout/dialogSlice";

// Local alias to simplify references
type ListContact = GetContactOutputType;

// Extended contact type with unread count for contact lists
type ContactWithUnreadCount = GetContactOutputType & {
  unreadCount?: number;
};

export type ChatWindowProps = {
  view: "open" | "collapsed" | "hidden";
  contactId: string;
  handleChange: React.Dispatch<
    React.SetStateAction<{
      view: "open" | "collapsed" | "hidden";
      contactId: string;
    }>
  >;
};

const generateAuthorName = (
  message: ListMessage,
  router: AppRouterInstance,
  selectedContact?: ListContact,
): DefaultMessageProps["author"] => {
  const subdomain = window.location.pathname.split("/")[1];

  const withAuthor = message as unknown as {
    author?: {
      referenceId: string;
      label: string;
      type: "WORKFLOW" | "CONTACT" | "MEMBER";
      image: { url?: string; seed?: string; icon?: string } | null;
    };
  };

  if (withAuthor.author) {
    const imageObj = withAuthor.author.image;
    const img =
      imageObj && "url" in imageObj
        ? imageObj.url
        : imageObj &&
            "icon" in imageObj &&
            typeof imageObj.icon === "string" &&
            imageObj.icon.startsWith("http")
          ? imageObj.icon
          : undefined;
    const seed = imageObj && "seed" in imageObj ? imageObj.seed : undefined;
    const icon: iconTypes | undefined =
      imageObj &&
      "icon" in imageObj &&
      typeof imageObj.icon === "string" &&
      !imageObj.icon.startsWith("http")
        ? (imageObj.icon as iconTypes)
        : undefined;
    let href: string | undefined;
    let handleClick: (() => void | Promise<void>) | undefined;

    if (withAuthor.author.type === "WORKFLOW") {
      const runId = withAuthor.author.referenceId;
      handleClick = async () => {
        await store.dispatch(getWorkflowRun({ workflowRunId: runId }));
        store.dispatch(
          openSideNavigation({
            position: "right",
            schema: "WORKFLOW_RUN",
            props: { width: "600px", mobileWidth: "80vw" },
          }),
        );
      };
    } else if (withAuthor.author.type === "CONTACT") {
      // Prefer linking to the associated instance if present
      const assoc = selectedContact?.contactAssociations?.[0];
      if (assoc?.instance?.model?.handlePlural && assoc?.instance?.id) {
        const handlePlural = assoc.instance.model.handlePlural;
        const instanceId = assoc.instance.id;
        if (handlePlural && instanceId && subdomain) {
          href = `${window.location.origin}/${subdomain}/${handlePlural}/${instanceId}`;
        }
      } else {
        const contactId = (message as { contactId?: string }).contactId;
        if (contactId && subdomain) {
          href = `${window.location.origin}/${subdomain}/conversations/${contactId}`;
        }
      }
    }

    return {
      id: withAuthor.author.referenceId,
      name: withAuthor.author.label,
      img,
      seed,
      icon,
      href,
      handleClick,
    };
  }

  if (message.memberId) {
    return {
      id: message.memberId,
      name: "Member",
    };
  }

  return {
    id: message.contactId || "contact",
    name: "Contact",
  };
};

export const generateCommChannelPopoverSchema = (
  selectedSubscription:
    | (ListContact["communicationChannelSubscriptions"][number] & {
        communicationChannelId?: string;
      })
    | undefined,
  selectedContact?: ListContact | undefined,
): ChatInputBarProps["popover"] => {
  return {
    label: selectedSubscription?.channel?.name || "",
    truncatedLabel: selectedSubscription?.channel?.name
      ? `${selectedSubscription?.channel?.name.substring(0, 5)}...`
      : "",
    value: selectedSubscription?.id || "",
    icon: (selectedSubscription?.channel?.icon || "RadioTower") as iconTypes,
    options:
      selectedContact?.communicationChannelSubscriptions.map(
        (subscription) => ({
          label: `${subscription.channel?.name || ""}`,
          description: `${subscription.channel?.name || ""}`,
          value: subscription.id,
          icon: (subscription.channel?.icon || "RadioTower") as iconTypes,
          selected: selectedSubscription?.id === subscription.id,
        }),
      ) || [],
    handleChange: (subscriptionId: string) => {
      if (!selectedContact) {
        return;
      }

      // find the subscription
      const subscription =
        selectedContact.communicationChannelSubscriptions.find(
          (s) => s.id == subscriptionId,
        );

      if (!subscription) {
        return;
      }

      void store.dispatch(
        selectSubscription({
          subscriptionId: subscription.id,
          contactId: selectedContact.id,
        }),
      );
    },
  };
};

export const generateStagedMessageSchema = (props: {
  message: string;
  isVisible: boolean;
  isLoading: boolean;
  currentMember: TeamMember | undefined;
  selectedContact?: ListContact | undefined;
  selectedSubscription:
    | (ListContact["communicationChannelSubscriptions"][number] & {
        communicationChannelId?: string;
      })
    | undefined;
}): StagedMessageProps | undefined => {
  const {
    message,
    isVisible,
    isLoading,
    currentMember,
    selectedContact,
    selectedSubscription,
  } = props;

  if (!isVisible || !message) {
    return undefined;
  }

  return {
    message,
    isLoading,
    isDisabled: !currentMember || !selectedSubscription,
    onSend: async (finalMessage: string) => {
      if (currentMember && selectedSubscription && selectedContact) {
        // First send the staged message
        store.dispatch(sendStagedMessage());

        // Then send the actual message
        await store.dispatch(
          sendMessage({
            member: currentMember,
            id: generateId("chatMessage"),
            contactId: selectedContact.id,
            message: finalMessage,
            communicationChannelId:
              selectedSubscription.communicationChannelId as string,
          }),
        );
      }
    },
    onReject: () => {
      store.dispatch(rejectStagedMessage());
    },
    onEdit: (editedMessage: string) => {
      store.dispatch(editStagedMessage(editedMessage));
    },
  };
};

export type ModelOption = {
  value: string;
  label: string;
  providerName: string;
};

export const generateSuggestedResponseSchema = (props: {
  message: string;
  isVisible: boolean;
  isLoading: boolean;
  selectedModelId: string | null;
  error: string | null;
  models: ModelOption[];
  currentMember: TeamMember | undefined;
  selectedContact?: ListContact | undefined;
  selectedSubscription:
    | (ListContact["communicationChannelSubscriptions"][number] & {
        communicationChannelId?: string;
      })
    | undefined;
  messages: ListMessage[];
  onModelPersist?: (modelId: string) => void;
}): SuggestedResponseProps | undefined => {
  const {
    message,
    isVisible,
    isLoading,
    selectedModelId,
    error,
    models,
    currentMember,
    selectedContact,
    selectedSubscription,
    messages,
    onModelPersist,
  } = props;

  if (!isVisible) {
    return undefined;
  }

  // Sort messages chronologically and include timestamps
  const sortedMessages = [...messages].sort((a, b) => {
    return new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime();
  });

  const conversationMessages = sortedMessages.map((m) => {
    const msgWithAuthor = m as unknown as {
      author?: { type: "WORKFLOW" | "CONTACT" | "MEMBER" };
    };
    const isFromContact = msgWithAuthor.author
      ? msgWithAuthor.author.type === "CONTACT"
      : !m.memberId;

    // Format timestamp as readable date/time
    const date = new Date(m.createdAt);
    const timestamp = date.toLocaleString("en-AU", {
      day: "numeric",
      month: "short",
      hour: "numeric",
      minute: "2-digit",
      hour12: true,
    });

    return {
      role: isFromContact ? ("user" as const) : ("assistant" as const),
      content: m.message,
      timestamp,
    };
  });

  return {
    message,
    models,
    selectedModelId,
    isLoading,
    error,
    isDisabled: !currentMember || !selectedSubscription,
    onModelChange: async (modelId: string) => {
      store.dispatch(setSuggestedResponseModel(modelId));
      onModelPersist?.(modelId);
      if (selectedContact) {
        await store.dispatch(
          generateSuggestedResponse({
            contactId: selectedContact.id,
            modelId,
            messages: conversationMessages,
          }),
        );
      }
    },
    onSend: async (finalMessage: string) => {
      if (currentMember && selectedSubscription && selectedContact) {
        store.dispatch(acceptSuggestedResponse());
        await store.dispatch(
          sendMessage({
            member: currentMember,
            id: generateId("chatMessage"),
            contactId: selectedContact.id,
            message: finalMessage,
            communicationChannelId:
              selectedSubscription.communicationChannelId ||
              selectedSubscription.channel.id,
          }),
        );
      }
    },
    onDismiss: () => {
      store.dispatch(dismissSuggestedResponse());
    },
    onEdit: (editedMessage: string) => {
      store.dispatch(editSuggestedResponse(editedMessage));
    },
    onCopy: async (text: string) => {
      try {
        await navigator.clipboard.writeText(text);
      } catch (err) {
        console.error("Failed to copy text:", err);
      }
    },
    onRegenerate: async () => {
      if (selectedContact) {
        await store.dispatch(
          generateSuggestedResponse({
            contactId: selectedContact.id,
            modelId: selectedModelId,
            messages: conversationMessages,
          }),
        );
      }
    },
  };
};

export const generateInputBarSchema = (props: {
  selectedSubscription:
    | (ListContact["communicationChannelSubscriptions"][number] & {
        communicationChannelId?: string;
      })
    | undefined;
  isSending: boolean;
  isLoading: boolean;
  currentMember: TeamMember | undefined;
  selectedContact?: ListContact | undefined;
  newMessage: { message: string; id: string; resetKey?: number };
}): ChatInputBarProps => {
  const {
    selectedSubscription,
    selectedContact,
    isSending,
    newMessage,
    currentMember,
    isLoading,
  } = props;

  return {
    popover: generateCommChannelPopoverSchema(
      selectedSubscription,
      selectedContact,
    ),
    actions: [],
    placeholder: "Type a message...",
    value: isSending ? "" : newMessage.message || "",
    resetKey: newMessage.resetKey,
    isDisabled:
      !currentMember ||
      !selectedSubscription ||
      (selectedSubscription as unknown as { optIn?: string })?.optIn ===
        "OPT_OUT",
    isLoading,
    handleTriggerAction: async (_actionId: string, _prompt: string) => {
      // TODO: Implement action handling
    },
    handleChange: (message) => {
      store.dispatch(updateMessage(message));
    },
    handleSend: async (message) => {
      if (currentMember && selectedSubscription && selectedContact) {
        await store.dispatch(
          sendMessage({
            member: currentMember,
            id: newMessage.id,
            contactId: selectedContact.id,
            message,
            communicationChannelId:
              selectedSubscription.communicationChannelId ||
              selectedSubscription.channel.id,
          }),
        );
      }
    },
  };
};

export const generateChatListSchema = (
  props: {
    contactId: string;
    isLoading: boolean;
    messages: ListMessage[];
    fetchedAt: string | undefined;
    selectedContact?: ListContact;
    pageRef?: React.RefObject<HTMLObjectElement>;
  },
  router: AppRouterInstance,
): ChatListProps => {
  const { contactId, isLoading, messages, pageRef, selectedContact } = props;

  const handleViewContent = async (contentURL: string) => {
    try {
      const html = await trpc.files.getContentByContentUrl.query({
        contentUrl: contentURL,
      });
      const fileName = contentURL.split("/").pop() || "content";
      store.dispatch(
        openDialog({
          schema: "VIEW_CONTENT",
          params: {
            html: String(html),
            title: "View Content",
            description: fileName,
          },
        }),
      );
    } catch (err) {
      console.error("Failed to load content:", err);
    }
  };

  const handleDownloadContent = async (fileIdOrContentUrl: string) => {
    try {
      // If it looks like a file ID (starts with "fl_"), use getDownloadUrlByFileId
      // Otherwise, treat it as a contentUrl
      let downloadUrl: string;
      if (fileIdOrContentUrl.startsWith("fl_")) {
        downloadUrl = await trpc.files.getDownloadUrlByFileId.query({
          fileId: fileIdOrContentUrl,
        });
      } else {
        downloadUrl = await trpc.files.getDownloadUrl.query({
          contentUrl: fileIdOrContentUrl,
        });
      }
      const response = await fetch(String(downloadUrl));
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = fileIdOrContentUrl.split("/").pop() || "file";
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
    } catch (err) {
      console.error("Failed to download content:", err);
    }
  };

  return {
    id: "chat-list",
    newChatMessage: "New Chat",
    isLoading,
    scrollContainer: pageRef?.current || null,
    handleLoadMoreMessages: async () => {
      // get first message
      const lastMessage = messages[messages.length - 1];

      if (!lastMessage) {
        return;
      }

      await store.dispatch(
        getContact({
          contactId,
          cursor: {
            id: lastMessage.id,
            date: lastMessage.createdAt,
            mode: "before",
          },
          concat: true,
        }),
      );
    },
    handleDownloadContent,
    components: generateMessages(messages, router, selectedContact, {
      handleViewContent,
      handleDownloadContent,
    }),
  };
};

export const generateContactListSchema = (props: {
  pageRef: React.RefObject<HTMLObjectElement>;
  contacts: ListContact[];
  totalContacts: number;
  cursor: {
    q: string;
    limit: number;
    cursorId?: string | null;
  };
  handleSelectChange: (contactId: string) => Promise<void> | void;
  selectedContactId?: string;
}): ListProps => {
  const { contacts, cursor, handleSelectChange, pageRef, selectedContactId } =
    props;

  return {
    title: "contact-list",
    infiniteScroll: {
      direction: "down",
      handleLoadMoreItems: () => {
        const last = contacts[contacts.length - 1];
        const nextCursorId = last?.id || null;
        void store.dispatch(
          getContacts({
            verified: true,
            q: cursor.q,
            limit: cursor.limit,
            cursorId: nextCursorId,
          }),
        );
      },
      scrollContainer: pageRef.current,
    },
    items: contacts.map((contact) => ({
      component: "ContactTile",
      mdSpan: 12,
      span: 12,
      lgSpan: 12,
      props: {
        unReadMessages: (contact as ContactWithUnreadCount).unreadCount || 0,
        isSelected: selectedContactId === contact.id,
        id: contact.id,
        name:
          contact.contactAssociations.find(
            (ca) => !ca.instance._meta?.label?.startsWith("ins_"),
          )?.instance._meta?.label ||
          contact.communicationChannelSubscriptions[0]?.identifierValue ||
          contact.id,
        lastMessage: contact.lastMessage
          ? contact.lastMessage
          : "No New Messages",
        timestamp: contact.lastActivityAt
          ? dayjs(contact.lastActivityAt).fromNow()
          : undefined,
        handleClick: () => {
          void handleSelectChange(contact.id);
        },
      },
    })),
    search: {
      id: "search-contacts",
      type: "text",
      value: "",
      leftIcon: "Search",
      placeholder: "Search Contacts...",
      handleChange: async (e) => {
        await store.dispatch(
          getContacts({
            limit: cursor.limit,
            q: e.target.value,
            cursorId: null,
            verified: true,
          }),
        );
      },
    },
  };
};

export const generateMessages = (
  messages: ListMessage[],
  router: AppRouterInstance,
  selectedContact?: ListContact,
  callbacks?: {
    handleViewContent?: (contentURL: string) => void | Promise<void>;
    handleDownloadContent?: (contentUrl: string) => void | Promise<void>;
  },
): ChatListProps["components"] => {
  // sort messages by chatId
  const sortedMessages = [...messages].sort((a, b) => {
    return a.chatId?.localeCompare(b.chatId || "") || 0;
  });

  // Build message components
  const components: ChatListProps["components"] = [
    ...sortedMessages.map((message, i): MessageComponent => {
      const isLastMessageOfChat = sortedMessages[i + 1]
        ? message.chatId !== sortedMessages[i + 1]?.chatId
        : true;
      const isLastMessageOfContact = !messages[i + 1];

      const _msgWithAuthor = message as unknown as {
        author?: { type: "WORKFLOW" | "CONTACT" | "MEMBER" };
      };
      const sentByContact = _msgWithAuthor.author
        ? _msgWithAuthor.author.type === "CONTACT"
        : !message.memberId;
      // const contactLabel =
      //   selectedContact?.contactAssociations?.[0]?.instance?._meta?.label ||
      //   selectedContact?.id ||
      //   "Contact";

      // Get contentURL and attachments from message
      const messageWithExtras = message as unknown as {
        contentURL?: string | null;
        attachments?: Array<{
          id: string;
          name: string;
          url: string;
          size?: number | null;
          mimeType?: string | null;
        }>;
      };

      const contentURL = messageWithExtras.contentURL;
      const attachments = messageWithExtras.attachments || [];

      // Build actions (e.g., open content URL) - only for text messages without contentURL
      const actions: {
        icon: iconTypes;
        title: string;
        handleClick: () => void | Promise<void>;
      }[] = [];

      // Error mapping if last attempt exists (delivery failure)
      const { lastAttemptAt, sentAt } = message as unknown as {
        lastAttemptAt?: string | null;
        sentAt?: string | null;
      };
      const error =
        lastAttemptAt && !sentAt ? { label: "Send failed" } : undefined;

      const baseProps = {
        id: message.id,
        isFinalMessageOfChat: isLastMessageOfChat && !isLastMessageOfContact,
        isMine: !sentByContact,
        isUnread: false,
        relativeTime: dayjs(message.createdAt).fromNow(),
        createdAt: message.createdAt,
        author: generateAuthorName(message, router, selectedContact),
        attachments,
        error,
      };

      // If contentURL exists, create a ContentMessageComponent
      if (contentURL) {
        return {
          type: "message",
          props: {
            type: "content",
            props: {
              ...baseProps,
              text: message.message,
              contentURL,
              handleViewContent: () =>
                callbacks?.handleViewContent?.(contentURL),
            },
          },
        };
      }

      // Otherwise, create a TextMessageComponent
      return {
        type: "message",
        props: {
          type: "text",
          props: {
            ...baseProps,
            text: message.message,
            actions,
          },
        },
      };
    }),
  ];

  // Sort components by creation date
  return components.sort((a, b) => {
    const aDate = new Date(a.props.props.createdAt).getTime();
    const bDate = new Date(b.props.props.createdAt).getTime();
    return aDate - bDate;
  });
};
